'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

interface AnimationContextType {
  prefersReducedMotion: boolean;
  enableAnimations: boolean;
  toggleAnimations: () => void;
}

const AnimationContext = createContext<AnimationContextType | undefined>(undefined);

export function AnimationProvider({ children }: { children: React.ReactNode }) {
  // Check user's preference for reduced motion
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [enableAnimations, setEnableAnimations] = useState(true);

  useEffect(() => {
    // Check if the user prefers reduced motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    // Update the state when the preference changes
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    // Get user's animation preference from localStorage
    const savedPreference = localStorage.getItem('enableAnimations');
    if (savedPreference !== null) {
      setEnableAnimations(savedPreference === 'true');
    } else {
      // If no preference is saved, disable animations if user prefers reduced motion
      setEnableAnimations(!mediaQuery.matches);
    }

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const toggleAnimations = () => {
    setEnableAnimations((prev) => {
      const newValue = !prev;
      localStorage.setItem('enableAnimations', String(newValue));
      return newValue;
    });
  };

  return (
    <AnimationContext.Provider value={{ prefersReducedMotion, enableAnimations, toggleAnimations }}>
      {children}
    </AnimationContext.Provider>
  );
}

export function useAnimation() {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error('useAnimation must be used within an AnimationProvider');
  }
  return context;
}
